import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/password-reset/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.status === 429) {
        setError('Too many reset requests. Please try again later.');
      } else if (res.status === 503) {
        setError('Password reset is temporarily unavailable. Please contact support.');
      } else if (res.ok) {
        setSent(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div className="global-watermark">
        <img src="/brand/nwg-logo-web.png" alt="NextWealth Global — Show Us the Problems. We Build the Solutions." />
      </div>
      <div style={styles.card}>
        <h1 style={styles.title}>NextWealth™ Global</h1>
        <p style={styles.subtitle}>PASSWORD RESET</p>

        {sent ? (
          <div style={styles.info} data-testid="reset-request-sent">
            If an account exists for that email, a password reset link has been sent.
            The link is valid for 30 minutes.
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={styles.form}>
            <p style={styles.hint}>
              Enter your account email and we&apos;ll send you a secure reset link.
            </p>
            <div style={styles.inputGroup}>
              <label style={styles.label}>EMAIL</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={styles.input}
                data-testid="forgot-password-email-input"
              />
            </div>
            {error && <div style={styles.error} data-testid="forgot-password-error">{error}</div>}
            <button type="submit" disabled={loading} style={styles.button} data-testid="forgot-password-submit-button">
              {loading ? 'SENDING...' : 'SEND RESET LINK'}
            </button>
          </form>
        )}

        <div style={styles.footer}>
          <Link to="/login" style={styles.link} data-testid="back-to-login-link">Back to login</Link>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', padding: 20 },
  card: { width: '100%', maxWidth: 400, background: 'rgba(17,17,17,0.95)', border: '1px solid rgba(255,215,0,0.4)', borderRadius: 8, padding: '36px 32px', zIndex: 1 },
  title: { color: '#FFD700', fontSize: 26, letterSpacing: 6, textAlign: 'center', margin: 0 },
  subtitle: { color: '#999', fontSize: 11, letterSpacing: 2, textAlign: 'center', margin: '6px 0 28px' },
  hint: { color: '#bbb', fontSize: 13, lineHeight: 1.6, margin: '0 0 18px' },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { color: '#FFD700', fontSize: 11, letterSpacing: 1.5 },
  input: { padding: '12px 14px', background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,215,0,0.3)', borderRadius: 4, color: '#fff', fontSize: 14, outline: 'none' },
  button: { padding: 14, background: '#FFD700', color: '#000', border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 'bold', letterSpacing: 1.5, cursor: 'pointer' },
  error: { color: '#ff6b6b', fontSize: 13 },
  info: { color: '#d4af37', fontSize: 14, lineHeight: 1.7, border: '1px solid rgba(255,215,0,0.3)', borderRadius: 4, padding: 16 },
  footer: { marginTop: 24, textAlign: 'center' },
  link: { color: '#FFD700', fontSize: 13, textDecoration: 'none', fontWeight: 'bold' },
};
